import { wildResponseHandler } from "@wildidea/tools/utils/wildResponseHandler.js";

import { IUserDataService } from "../../../src/tracking/domain/IUserDataService.js";

import {
  API_PREFIX,
  GetTilesetInput,
  GetUserMessagesInput,
  GetUserMessagesResponse,
  GetUserTracksInput,
  GetUserTracksResponse,
  ITrackingProviderId,
  Tileset,
  TrackingProviderId,
  UserStatus,
} from "./model.js";

export interface VectorTileServiceClientConfig {
  baseUri?: string;
}

export class VectorTileServiceClient {
  private readonly baseUri: string;
  protected baseHeaders: Record<string, string> = {
    Accept: "application/json",
  };

  constructor({ baseUri = "" }: VectorTileServiceClientConfig = {}) {
    this.baseUri = baseUri;
  }

  getTilesetEndpoint(input: GetTilesetInput) {
    return `${this.baseUri}${API_PREFIX}/tilesets/${input.name}${input.name === "user" ? `/${input.options.userId}` : ""}`;
  }

  async getTileset(input: GetTilesetInput): Promise<Tileset> {
    return wildResponseHandler(
      fetch(this.getTilesetEndpoint(input), { headers: this.baseHeaders }),
      `${VectorTileServiceClient.name}.${this.getTileset.name}`,
    );
  }
}

export interface UserDataServiceClientConfig {
  /**
   * Prepended to every API call, e.g. `https://api.dev.onemap.earth`. Required when
   * the client runs outside a browser (tests, scripts) since relative-URL resolution
   * is undefined in Node. Optional in a browser where calls resolve to the current
   * origin's `/api/...` path.
   */
  baseUri?: string;
}

export class UserDataServiceClient implements IUserDataService {
  private readonly baseUri: string;
  protected baseHeaders: Record<string, string> = {
    Accept: "application/json",
  };

  constructor({ baseUri = "" }: UserDataServiceClientConfig = {}) {
    this.baseUri = baseUri;
  }

  async getStatus(input: ITrackingProviderId): Promise<UserStatus> {
    return wildResponseHandler(
      fetch(
        `${this.baseUri}${API_PREFIX}/users/${new TrackingProviderId(input).toString()}/status`,
        {
          headers: this.baseHeaders,
        },
      ),
      `${UserDataServiceClient.name}.${this.getStatus.name}`,
    );
  }

  async getMessages(
    input: GetUserMessagesInput,
  ): Promise<GetUserMessagesResponse> {
    const searchParams = new URLSearchParams(
      Object.entries({
        startDate: input.timeRange?.startDate?.toISOString(),
        endDate: input.timeRange?.endDate?.toISOString(),
        eventIds: input.eventIds
          ?.map((tsid) => new TrackingProviderId(tsid).toString())
          .join(","),
      }).filter<[string, string]>(
        (entry): entry is [string, string] => !!entry[1],
      ),
    );

    return wildResponseHandler(
      fetch(
        `${this.baseUri}${API_PREFIX}/users/${new TrackingProviderId(input.user).toString()}/messages${searchParams.size ? `?${searchParams}` : ""}`,
        { headers: this.baseHeaders },
      ),
      `${UserDataServiceClient.name}.${this.getMessages.name}`,
    );
  }

  async getTracks(input: GetUserTracksInput): Promise<GetUserTracksResponse> {
    const searchParams = new URLSearchParams(
      Object.entries({
        startDate: input.timeRange?.startDate?.toISOString(),
        endDate: input.timeRange?.endDate?.toISOString(),
        trackIds: input.trackIds?.join(","),
      }).filter<[string, string]>(
        (entry): entry is [string, string] => !!entry[1],
      ),
    );

    return wildResponseHandler(
      fetch(
        `${this.baseUri}${API_PREFIX}/users/${new TrackingProviderId(input.user).toString()}/tracks${searchParams.size ? `?${searchParams}` : ""}`,
        { headers: this.baseHeaders },
      ),
      `${UserDataServiceClient.name}.${this.getTracks.name}`,
    );
  }
}

export * from "./model.js";
